import { useRef, useEffect } from "react";

// Original radar-scan hero animation: a sweep line detects points, lights them up,
// draws fleeting connections, and a padlock pulses at the core.
// Conveys scanning, connecting the dots, and securing. GDPRLocal palette + company pink.
// Pass active for the audit loading screen: faster, busier, dual beams.
const ASPHALT = "#364F6B";
const AQUA = "#2FE1EC";
const PINK = "#FC5185";
const LINE = "#E4E7EC";

export const ScanAnimation = ({ active = false }) => {
    const canvasRef = useRef(null);
    const rafRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        let W = 0, H = 0, cx = 0, cy = 0, R = 0, dpr = 1;
        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            W = rect.width; H = rect.height;
            canvas.width = W * dpr; canvas.height = H * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            cx = W / 2; cy = H / 2; R = Math.min(W, H) / 2 - 8;
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        // tuning by mode
        const SWEEP_SPEED = active ? 0.023 : 0.011;
        const ORBIT_MULT = active ? 2.0 : 1.0;
        const DETECT_WIN = active ? 0.10 : 0.06;
        const DECAY = active ? 0.978 : 0.965;
        const N = active ? 20 : 15;

        const pts = Array.from({ length: N }, () => ({
            angle: Math.random() * Math.PI * 2,
            radius: 0.22 + Math.random() * 0.68,
            va: (Math.random() - 0.5) * 0.0012 * ORBIT_MULT,
            glow: 0,
            size: 2 + Math.random() * 2.4,
            color: Math.random() < 0.42 ? PINK : AQUA,
        }));

        let sweep = 0;
        const norm = (a) => { while (a > Math.PI) a -= Math.PI * 2; while (a < -Math.PI) a += Math.PI * 2; return a; };
        const rrect = (x, y, w, h, r) => {
            if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); }
            else { ctx.beginPath(); ctx.rect(x, y, w, h); }
        };
        const beam = (ang, color) => {
            const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
            const rgb = color === PINK ? "252,81,133" : "47,225,236";
            grad.addColorStop(0, `rgba(${rgb},${active ? 0.34 : 0.28})`);
            grad.addColorStop(1, `rgba(${rgb},0)`);
            ctx.save();
            ctx.beginPath(); ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, R, ang - 0.7, ang);
            ctx.closePath(); ctx.fillStyle = grad; ctx.fill();
            ctx.restore();
            ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.globalAlpha = 0.85;
            ctx.beginPath(); ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.cos(ang) * R, cy + Math.sin(ang) * R); ctx.stroke();
            ctx.globalAlpha = 1;
        };

        const drawLock = (t) => {
            ctx.save();
            ctx.translate(cx, cy);
            // dual pulsing rings (aqua + pink, offset phase)
            [[AQUA, 0], [PINK, 260]].forEach(([col, off]) => {
                const pr = 22 + (Math.sin((t + off) / (active ? 360 : 520)) + 1) * 7;
                ctx.strokeStyle = col;
                ctx.globalAlpha = Math.max(0, 0.5 - (pr - 22) / 40);
                ctx.lineWidth = 2;
                ctx.beginPath(); ctx.arc(0, 0, pr, 0, Math.PI * 2); ctx.stroke();
            });
            ctx.globalAlpha = 1;
            ctx.fillStyle = ASPHALT;
            ctx.beginPath(); ctx.arc(0, 0, 20, 0, Math.PI * 2); ctx.fill();
            // padlock
            ctx.strokeStyle = AQUA; ctx.lineWidth = 2.4; ctx.lineCap = "round";
            ctx.beginPath(); ctx.arc(0, -3, 5.5, Math.PI, 0); ctx.stroke();
            ctx.fillStyle = AQUA;
            rrect(-7, -2.5, 14, 11, 2); ctx.fill();
            ctx.fillStyle = ASPHALT;
            ctx.beginPath(); ctx.arc(0, 2.5, 1.6, 0, Math.PI * 2); ctx.fill();
            ctx.restore();
        };

        const frame = (t) => {
            ctx.clearRect(0, 0, W, H);

            ctx.strokeStyle = LINE; ctx.lineWidth = 1;
            for (let i = 1; i <= 4; i++) {
                ctx.globalAlpha = 0.7;
                ctx.beginPath(); ctx.arc(cx, cy, (R * i) / 4, 0, Math.PI * 2); ctx.stroke();
            }
            ctx.globalAlpha = 0.5;
            for (let i = 0; i < 8; i++) {
                const a = (i / 8) * Math.PI * 2;
                ctx.beginPath(); ctx.moveTo(cx, cy);
                ctx.lineTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R); ctx.stroke();
            }
            ctx.globalAlpha = 1;

            // rotating outer dashed ring
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate((t / (active ? 5000 : 9000)) % (Math.PI * 2));
            ctx.strokeStyle = active ? PINK : AQUA; ctx.globalAlpha = 0.35; ctx.lineWidth = 1.5;
            ctx.setLineDash([2, 10]);
            ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.stroke();
            ctx.setLineDash([]);
            ctx.restore();
            ctx.globalAlpha = 1;

            // sweep beams
            sweep += SWEEP_SPEED;
            beam(sweep, AQUA);
            if (active) beam(sweep + Math.PI, PINK); // second beam opposite in active mode

            // update + draw points
            const lit = [];
            for (const p of pts) {
                p.angle += p.va;
                const d1 = Math.abs(norm(sweep - p.angle));
                const d2 = active ? Math.abs(norm(sweep + Math.PI - p.angle)) : 99;
                if (d1 < DETECT_WIN || d2 < DETECT_WIN) p.glow = 1;
                p.glow *= DECAY;
                const px = cx + Math.cos(p.angle) * p.radius * R;
                const py = cy + Math.sin(p.angle) * p.radius * R;
                p._x = px; p._y = py;
                if (p.glow > 0.35) lit.push(p);
                const g = p.glow;
                ctx.beginPath();
                ctx.fillStyle = g > 0.1 ? p.color : "#9aa7b8";
                ctx.globalAlpha = 0.45 + g * 0.55;
                ctx.arc(px, py, p.size + g * 3, 0, Math.PI * 2); ctx.fill();
                if (g > 0.4) {
                    ctx.globalAlpha = g * 0.8;
                    ctx.strokeStyle = p.color; ctx.lineWidth = 1.5;
                    ctx.beginPath(); ctx.arc(px, py, p.size + 6 + (1 - g) * 10, 0, Math.PI * 2); ctx.stroke();
                }
                ctx.globalAlpha = 1;
            }

            // connect currently-lit points, gradient between their colours
            lit.sort((a, b) => a.angle - b.angle);
            ctx.lineWidth = 1.4; ctx.setLineDash([3, 4]);
            for (let i = 0; i < lit.length - 1; i++) {
                const a = lit[i], b = lit[i + 1];
                const lg = ctx.createLinearGradient(a._x, a._y, b._x, b._y);
                lg.addColorStop(0, a.color); lg.addColorStop(1, b.color);
                ctx.strokeStyle = lg;
                ctx.globalAlpha = Math.min(a.glow, b.glow) * 0.55;
                ctx.beginPath(); ctx.moveTo(a._x, a._y); ctx.lineTo(b._x, b._y); ctx.stroke();
            }
            ctx.setLineDash([]); ctx.globalAlpha = 1;

            drawLock(t);
            rafRef.current = requestAnimationFrame(frame);
        };
        rafRef.current = requestAnimationFrame(frame);

        return () => { cancelAnimationFrame(rafRef.current); ro.disconnect(); };
    }, [active]);

    return (
        <div className="relative w-full max-w-[440px] aspect-square mx-auto" data-testid="scan-animation" data-active={active ? "true" : "false"}>
            <div className={`absolute inset-0 rounded-full blur-2xl transition-opacity ${active ? "bg-gradient-to-br from-aqua/40 to-[#FC5185]/30" : "bg-aqua-tint/40"}`} />
            <canvas ref={canvasRef} className="relative w-full h-full" />
        </div>
    );
};
